import React from 'react';
import { X, Palette, Shield, CheckCircle } from 'lucide-react';
import { useTheme } from '../theme/ThemeContext';

const ThemeSelector = ({ isOpen, onClose }) => {
  const { currentTheme, changeTheme, availableThemes } = useTheme();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="w-6 h-6 text-slate-700" />
            <div>
              <h3 className="text-lg font-bold text-slate-800">Brand Theme Settings</h3>
              <p className="text-sm text-slate-600">
                Customize your dashboard appearance
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-slate-600 mb-6">
            Select a color theme to apply across your entire dashboard, including
            navigation, buttons, and accents.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.entries(availableThemes).map(([key, themeData]) => (
              <button
                key={key}
                onClick={() => changeTheme(key)}
                className={`relative p-6 rounded-xl border-2 transition-all duration-300 ${
                  currentTheme === key
                    ? 'border-slate-700 bg-slate-50 shadow-lg scale-105'
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
                }`}
              >
                {currentTheme === key && (
                  <div className="absolute top-3 right-3">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                )}

                <div className="mb-4">
                  <h4 className="font-bold text-slate-800 mb-1">{themeData.name}</h4>
                  <p className="text-xs text-slate-600">Applied to all components</p>
                </div>

                <div className="space-y-3">
                  <div
                    className={`h-8 rounded-lg bg-gradient-to-r ${themeData.gradient} shadow-sm`}
                  ></div>

                  <div className="flex gap-2">
                    <div
                      className="w-full h-6 rounded"
                      style={{ backgroundColor: themeData.primary[500] }}
                    />
                    <div
                      className="w-full h-6 rounded"
                      style={{ backgroundColor: themeData.primary[600] }}
                    />
                    <div
                      className="w-full h-6 rounded"
                      style={{ backgroundColor: themeData.secondary[500] }}
                    />
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  Brand Consistency
                </p>
                <p className="text-xs text-blue-700">
                  Your selected theme will apply to all pages, ensuring consistent
                  branding across the entire application.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThemeSelector;
